import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { ArrowsClockwise, ListMagnifyingGlass, CaretLeft, CaretRight } from "@phosphor-icons/react";

const PAGE_SIZE = 50;

const ACTION_LABELS = {
  create: "Создание",
  update: "Изменение",
  delete: "Удаление",
};

const ENTITY_LABELS = {
  order: "Заказ",
  warehouse_item: "Склад",
  subscription: "Подписка",
  expense: "Расход",
  file: "Файл",
};

const ACTION_COLORS = {
  create: "text-[#00994C] border-[#00994C]",
  update: "text-[#0047FF] border-[#0047FF]",
  delete: "text-[#FF2A00] border-[#FF2A00]",
};

function formatTs(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AuditLogPage() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0); // 0-based
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (targetPage) => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/audit/log", {
        params: { skip: targetPage * PAGE_SIZE, limit: PAGE_SIZE },
      });
      setEntries(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error("audit load failed", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load(page);
  }, [load, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min(total, page * PAGE_SIZE + entries.length);

  return (
    <div className="px-8 py-8 max-w-6xl">
      <div className="mb-8 flex items-center justify-between gap-4">
        <div>
          <div className="mono-label mb-1">АДМИНИСТРИРОВАНИЕ</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Журнал действий</h1>
        </div>
        <button
          type="button"
          onClick={() => load(page)}
          disabled={refreshing}
          data-testid="audit-refresh"
          className="inline-flex items-center gap-2 border border-[#18181b] px-3 py-2 text-sm hover:bg-[#18181b] hover:text-white transition-colors duration-150 disabled:opacity-50"
        >
          <ArrowsClockwise size={14} weight="bold" className={refreshing ? "animate-spin" : ""} />
          Обновить
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-[#52525b] mono-label">ЗАГРУЗКА...</div>
      ) : total === 0 ? (
        <div className="border border-dashed border-[#d4d4d8] bg-white p-10 text-center text-sm text-[#52525b]">
          <ListMagnifyingGlass size={28} weight="duotone" className="mx-auto mb-3 text-[#a1a1aa]" />
          Журнал пуст. Действия пользователей будут появляться здесь автоматически.
        </div>
      ) : (
        <>
          <div className="border border-[#d4d4d8] bg-white overflow-hidden">
            <table className="w-full" data-testid="audit-table">
              <thead className="bg-[#f5f5f4] border-b border-[#d4d4d8]">
                <tr>
                  <th className="text-left px-4 py-2 mono-label">ВРЕМЯ</th>
                  <th className="text-left px-4 py-2 mono-label">ПОЛЬЗОВАТЕЛЬ</th>
                  <th className="text-left px-4 py-2 mono-label">ДЕЙСТВИЕ</th>
                  <th className="text-left px-4 py-2 mono-label">РАЗДЕЛ</th>
                  <th className="text-left px-4 py-2 mono-label">ОПИСАНИЕ</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr
                    key={e.id}
                    data-testid={`audit-row-${e.id}`}
                    className="border-b border-[#d4d4d8] last:border-b-0 align-top"
                  >
                    <td className="px-4 py-2 text-xs font-mono text-[#52525b] whitespace-nowrap">
                      {formatTs(e.timestamp)}
                    </td>
                    <td className="px-4 py-2 text-sm font-mono">{e.username}</td>
                    <td className="px-4 py-2 text-xs whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 border ${ACTION_COLORS[e.action] || "text-[#52525b] border-[#52525b]"} mono-label`}
                      >
                        {ACTION_LABELS[e.action] || e.action.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-[#52525b] whitespace-nowrap">
                      {ENTITY_LABELS[e.entity] || e.entity}
                    </td>
                    <td className="px-4 py-2 text-sm">{e.summary}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between gap-4">
            <div className="text-xs mono-label text-[#52525b]" data-testid="audit-pagination-info">
              {from}–{to} ИЗ {total}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || refreshing}
                data-testid="audit-prev-page"
                className="inline-flex items-center gap-1 border border-[#18181b] px-3 py-1.5 text-xs hover:bg-[#18181b] hover:text-white transition-colors duration-150 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#18181b]"
              >
                <CaretLeft size={12} weight="bold" />
                Назад
              </button>
              <span className="text-xs mono-label tabular-nums" data-testid="audit-page-indicator">
                {page + 1} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1 || refreshing}
                data-testid="audit-next-page"
                className="inline-flex items-center gap-1 border border-[#18181b] px-3 py-1.5 text-xs hover:bg-[#18181b] hover:text-white transition-colors duration-150 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-[#18181b]"
              >
                Далее
                <CaretRight size={12} weight="bold" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
